<template>
  <div>
    <div class="container page-top">
      <div class="intro">
        <h1 class="h-page">{{ t('solutions.title') }}</h1>
        <p class="lead">{{ t('solutions.sub') }}</p>
      </div>

      <div class="search" role="search">
        <input
          v-model="query"
          type="search"
          class="search-input"
          :placeholder="t('solutions.search')"
          :aria-label="t('solutions.search')"
        >
        <span class="count" aria-live="polite">{{ filtered.length }} {{ t('solutions.results_label') }}</span>
      </div>

      <div class="grid">
        <SolutionCard
          v-for="solution in filtered"
          :key="solution.slug"
          :solution="solution"
          :min-height="260"
          :title-size="28"
        />
        <div class="build-card">
          <h3 class="card-title build-title">{{ t('solutions.build_title') }}</h3>
          <div>
            <p class="build-body">{{ t('solutions.build_body') }}</p>
            <PillButton to="/contact" variant="white" size="md">{{ t('cta') }}</PillButton>
          </div>
        </div>
      </div>

      <p v-if="!filtered.length" class="empty">{{ ui('no_results') }}</p>
    </div>

    <CtaBand />
  </div>
</template>

<script setup>
import { computed, ref } from 'vue'
import PillButton from '../components/PillButton.vue'
import SolutionCard from '../components/SolutionCard.vue'
import CtaBand from '../components/CtaBand.vue'
import { useLocale } from '../composables/useLocale'
import { solutions } from '../data'

const { t, ui, pick } = useLocale()

const query = ref('')

// Matches the name, description and audience in the language being read.
const filtered = computed(() => {
  const q = query.value.trim().toLowerCase()
  if (!q) {
    return solutions
  }
  return solutions.filter((solution) => ['name', 'description', 'audience']
    .map((key) => pick(solution, key) || '')
    .join(' ')
    .toLowerCase()
    .includes(q))
})
</script>

<style scoped>
.intro {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(min(100%, 420px), 1fr));
  gap: clamp(24px, 4vw, 56px);
  align-items: end;
  margin-bottom: 40px;
}

.search {
  display: flex;
  align-items: center;
  gap: 12px;
  background: var(--surface);
  border-radius: var(--radius-pill);
  padding: 6px;
  padding-inline-start: 24px;
  margin-bottom: 24px;
  max-width: 560px;
}

.search-input {
  flex: 1;
  min-width: 0;
  font-size: 16px;
  background: none;
  border: none;
  outline: none;
  padding: 12px 0;
  color: var(--ink);
  -webkit-appearance: none;
  appearance: none;
}

.search:focus-within {
  outline: 2px solid var(--ink);
  outline-offset: 2px;
}

.count {
  background: #fff;
  border-radius: var(--radius-pill);
  padding: 10px 16px;
  font-size: 14px;
  color: var(--muted);
  white-space: nowrap;
}

.grid {
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(min(100%, 300px), 1fr));
  gap: 16px;
}

.build-card {
  background: var(--dark);
  color: #fff;
  border-radius: var(--radius-card);
  padding: 28px;
  min-height: 260px;
  display: flex;
  flex-direction: column;
  justify-content: space-between;
  gap: 24px;
}

.build-title {
  font-size: 32px;
}

.build-body {
  font-size: 16px;
  line-height: 1.55;
  color: var(--on-dark-muted);
  margin-bottom: 20px;
}

.empty {
  margin-top: 24px;
  color: var(--muted);
  font-size: 17px;
}
</style>
